import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { DropdownFoldMenu } from '#/components/Dropdown/menus/Fold/DropdownFoldMenu';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';
import { isAdmin } from '#/utils/userEnvironment';

import { participateDropdownDataByLocale } from './data/participateDropdownDataByLocale';
import {
  participateDropdownDataByResourcesItemType,
  participateDropdownDataBySearchItemType,
} from './ParticipateDropdownConstants';

const triggerTextByLocale: Record<
  I18nLocale.ES,
  { text: string; href: string }
> = {
  es: {
    text: 'Participa',
    href: '/es/dias/participa',
  },
};

type Props = {
  locale: I18nLocale.ES;
  user: ApiUser | null;
};

export function ParticipateDropdown({ locale, user }: Props) {
  const isAdminUser = isAdmin(user);
  const { search, resources } = participateDropdownDataByLocale[locale];

  const { text, href } = triggerTextByLocale[locale];

  return (
    <Dropdown
      trigger={
        <LayoutHeaderButton
          data={{ type: 'inert' }}
          desktopData={{
            type: 'link',
            href,
            clickActions: {
              track: {
                name: 'Participate',
                properties: { context: 'Header' },
              },
            },
          }}
          variant="grey"
          qaId="page-header-participate-dropdown-trigger"
        >
          {text} <DropdownChevron />
        </LayoutHeaderButton>
      }
      desktopTriggersOnHover
    >
      <DropdownFoldMenu
        left={{
          title: search.title,
          items: search.items.map((searchItem) => {
            const { iconName, qaId, trackClickedName } =
              participateDropdownDataBySearchItemType[searchItem.type];

            return {
              iconName,
              title: searchItem.title,
              href:
                isAdminUser && searchItem.adminUserHref
                  ? searchItem.adminUserHref
                  : searchItem.href,
              qaId,
              trackClicked: {
                name: trackClickedName,
                context: 'Header Dropdown > Participate',
              },
            };
          }),
        }}
        right={{
          title: resources.title,
          items: resources.items.map((resourceItem) => {
            const { image, imageHover, qaId, trackClickedName } =
              participateDropdownDataByResourcesItemType[resourceItem.type];

            return {
              image,
              imageHover,
              title: resourceItem.title,
              description: resourceItem.description,
              href: resourceItem.href,
              qaId,
              trackClicked: {
                name: trackClickedName,
                context: 'Header Dropdown > Participate',
              },
            };
          }),
        }}
      />
    </Dropdown>
  );
}
